import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMessages, useSendMessage } from '../../hooks/useMessages';
import { useWebSocket } from '../../hooks/useWebSocket';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ApiError } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { CodeTab } from '../../types/project.types';

interface ChatPanelProps {
  projectId: string;
  onOpenFile?: (file: CodeTab) => void;
}

export function ChatPanel({ projectId, onOpenFile }: ChatPanelProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { data: messages, isLoading } = useMessages(projectId);
  const sendMessage = useSendMessage(projectId);
  const [draft, setDraft] = useState('');
  const [queuedPrompt, setQueuedPrompt] = useState<string | null>(null);
  const [rateLimitSecondsLeft, setRateLimitSecondsLeft] = useState(0);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isRateLimited = rateLimitSecondsLeft > 0;

  // WebSocket handler for generation_error and message_created events.
  const handleWsMessage = useCallback(
    (type: string, data: unknown) => {
      if (type === 'generation_error') {
        const payload = data as { error?: string };
        setGenerationError(payload?.error ?? 'Generation failed');
      } else if (type === 'message_created') {
        queryClient.invalidateQueries({ queryKey: ['messages', projectId] });
      }
    },
    [projectId, queryClient],
  );

  useWebSocket({ projectId, onMessage: handleWsMessage });

  // Start countdown when the mutation error is a 429.
  useEffect(() => {
    const err = sendMessage.error;
    if (!(err instanceof ApiError) || err.status !== 429) return;
    const seconds = err.retryAfter ?? 60;
    showToast('Generation limit reached. Please wait before trying again.', 'error');
    setRateLimitSecondsLeft(seconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setRateLimitSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, [sendMessage.error, showToast]);

  // Clean up interval on unmount.
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const handleSend = (content: string) => {
    setGenerationError(null);
    sendMessage.mutate({ content });
    setDraft('');
  };

  useEffect(() => {
    if (!queuedPrompt || sendMessage.isPending) {
      return;
    }
    handleSend(queuedPrompt);
    setQueuedPrompt(null);
    // handleSend is stable in behaviour but recreated per render; adding it
    // would re-fire the effect without a queued prompt change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedPrompt, sendMessage.isPending]);

  const handleExamplePromptSelect = (prompt: string) => {
    setDraft(prompt);
    setQueuedPrompt(prompt);
  };

  if (isLoading && !messages) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const genericError =
    sendMessage.error && !(sendMessage.error instanceof ApiError && sendMessage.error.status === 429)
      ? sendMessage.error.message || 'Failed to send — please try again.'
      : null;

  return (
    <div className="flex flex-col h-full">
      <MessageList
        messages={messages ?? []}
        isLoading={sendMessage.isPending}
        onExamplePromptSelect={handleExamplePromptSelect}
        onOpenFile={onOpenFile}
      />

      {isRateLimited && (
        <div className="mx-4 mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          Generation limit reached. Try again in{' '}
          <span className="font-semibold tabular-nums">{rateLimitSecondsLeft}s</span>.
        </div>
      )}

      {generationError && (
        <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          Generation failed: {generationError}
        </div>
      )}

      {genericError && !generationError && (
        <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {genericError}
        </div>
      )}

      <MessageInput
        value={draft}
        onChange={setDraft}
        onSend={handleSend}
        disabled={sendMessage.isPending || isRateLimited}
      />
    </div>
  );
}
